import { useContext } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiContext } from '../../../shared/contexts';
import { usePollingJob } from '@shared/hooks';
import { TBulkExport } from '../types/bulkExports.types';
import { downloadFile } from '../../files';
import { TVersionPreference } from '../types/items.types';

/**
 * Hook to poll the status of an existing bulk export job until completion.
 */
export const usePollBulkExport = () =>
  usePollingJob<TBulkExport>({
    queryKeyPrefix: 'bulk-export',
    endpoint: '/items/export',
    progressMessage: 'Exporting...',
    successMessage: 'Export complete!',
    failureMessage: 'Export failed. We have contacted your support team.',
    onCompleted: (job) => downloadFile(job.url!, job.mimetype!, job.filename!),
  });

/**
 * Hook to start a bulk export job asynchronously.
 */
export const useBulkExport = () => {
  const { apiClient, handleApiError, queryClient } = useContext(ApiContext);

  const { isPolling, setJobId } = usePollBulkExport();

  const mutationFn = async (variables: { itemIds: string[]; mode: TVersionPreference }): Promise<TBulkExport> => {
    if (isPolling) throw new Error('Export already in progress');
    // starts the background job
    const { data } = await apiClient.post<TBulkExport>('/items/export', variables);
    return data;
  };

  const mutation = useMutation({
    mutationFn,
    onSuccess: (data) => {
      setJobId(data._id);
      queryClient.setQueryData([`bulk-export-${data._id}`], () => data);
    },
    onError: handleApiError('Export initiation failed!', true),
  });

  return { ...mutation, isPending: mutation.isPending || isPolling };
};
